/**
 * Per-locale POSTAL_CODE validation regexes — the **fallback** source for postcode validation,
 * used when a POSTAL_CODE question carries no config regex of its own. The survey's own
 * `configuration.validation.regexPattern` takes priority; this map fills in behind it (and an
 * operator can set the config regex to ".*" to accept postcodes from any country). See
 * Question.getValidationConfiguration().
 *
 * Keyed by station locale `languageTag` (the value behind `apiClient.locale.languageTag`,
 * derived by the API from the station's installation-country locale, e.g. "pl_PL"). Patterns
 * are authored **correctly** here (real `\d` / `\s`) and consumed as-is — no runtime repair.
 */
export const POSTCODE_REGEX_BY_LOCALE = {
    en_AU: "^[0-9]{4}$",
    en_GB:
        "^(([A-Z]{1,2}[0-9][A-Z0-9]?|ASCN|STHL|TDCU|BBND|[BFS]IQQ|PCRN|TKCA) [0-9][A-Z]{2}|BFPO [0-9]{1,4}|(KY[0-9]|MSR|VG|AI)[ -]?[0-9]{4}|[A-Z]{2} [0-9]{2}|GE CX|GIR 0A{2}|SAN TA1)$",
    en_IE:
        "^(a(4[125s]|6[37]|7[5s]|[8b][1-6s]|9[12468b])|c1[5s]|d([0o][1-9sb]|1[0-8osb]|2[024o]|6w)|e(2[15s]|3[24]|4[15s]|[5s]3|91)|f(12|2[368b]|3[15s]|4[25s]|[5s][26]|9[1-4])|h(1[2468b]|23|[5s][34]|6[25s]|[79]1)|k(3[246]|4[5s]|[5s]6|67|7[8b])|n(3[79]|[49]1)|p(1[247]|2[45s]|3[126]|4[37]|[5s][16]|6[17]|7[25s]|[8b][15s])|r(14|21|3[25s]|4[25s]|[5s][16]|9[35s])|t(12|23|34|4[5s]|[5s]6)|v(1[45s]|23|3[15s]|42|9[2-5s])|w(12|23|34|91)|x(3[5s]|42|91)|y(14|2[15s]|3[45s]))[ ]?[abcdefhknoprtsvwxy0-9]{4}$",
    en_CH: "^[1-9][0-9]{3}$",
    sk_SK: "^\\d{3}\\s?\\d{2}$",
    da_DK: "^[1-9][0-9]{3}$",
    sv_SE: "^\\d{3}\\s?\\d{2}$",
    lv_LV: "^LV-\\d{4}$",
    pl_PL: "^\\d{2}-\\d{3}$",
    de_DE: "^\\d{5}$",
    cs_CZ: "^\\d{3}\\s?\\d{2}$",
    es_ES: "^\\d{5}$",
    de_CH: "^([1-9][0-9]{3})$",
    it_IT: "^\\d{5}$",
    fr_FR: "^\\d{5}$",
    nl_NL: "^\\d{4}\\s?[A-Z]{2}$",
    fi_FI: "^\\d{5}$",
    nn_NO: "^\\d{4}$",
    nl_BE: "^\\d{4}$"
};

/**
 * i18n key for the postcode format error message per campaign locale. Reuses the canonical
 * `surveys.regex_error.<country>_post_code` keys already used by the per-locale PROFILE
 * surveys (so translations are shared) and adds the three English-speaking locales that
 * lacked one (UK, Australia, Ireland). The key is resolved with the user's display language
 * at render time, so e.g. a Ukrainian user on a GB campaign sees the Ukrainian translation
 * of "enter a valid UK postcode". English source strings live in
 * /Users/mz/Documents/simplelocalize/App/en-GB-postal-code-errors.json.
 */
export const POSTCODE_ERROR_KEY_BY_LOCALE = {
    en_AU: "surveys.regex_error.australian_post_code",
    en_GB: "surveys.regex_error.uk_post_code",
    en_IE: "surveys.regex_error.irish_post_code",
    en_CH: "surveys.regex_error.switzerland_post_code",
    sk_SK: "surveys.regex_error.slovakian_post_code",
    da_DK: "surveys.regex_error.danish_post_code",
    sv_SE: "surveys.regex_error.swedish_post_code",
    lv_LV: "surveys.regex_error.latvian_post_code",
    pl_PL: "surveys.regex_error.polish_post_code",
    de_DE: "surveys.regex_error.german_post_code",
    cs_CZ: "surveys.regex_error.czechia_post_code",
    es_ES: "surveys.regex_error.spain_post_code",
    de_CH: "surveys.regex_error.switzerland_post_code",
    it_IT: "surveys.regex_error.italy_post_code",
    fr_FR: "surveys.regex_error.france_post_code",
    nl_NL: "surveys.regex_error.netherlands_post_code",
    fi_FI: "surveys.regex_error.finland_post_code",
    nn_NO: "surveys.regex_error.norway_post_code",
    nl_BE: "surveys.regex_error.belgium_post_code"
};

/**
 * Normalize a campaign locale tag to the underscore form used as a map key. The generic
 * English locale "en" is treated as a catch-all that gets NO postcode validation (postcodes
 * could be from anywhere) — avoid setting a station/campaign locale to "en".
 *
 * @param {string|null|undefined} languageTag
 * @returns {string|null} underscore tag, or null for falsy/generic "en"
 */
function normalizeLocaleTag(languageTag) {
    if (!languageTag) {
        return null;
    }
    const underscoreTag = languageTag.replace("-", "_");
    return underscoreTag === "en" ? null : underscoreTag;
}

/**
 * Returns the POSTAL_CODE regex for a station locale, or null when the locale is
 * unknown/missing/generic-English. Accepts both the DB underscore form ("pl_PL") and the
 * SimpleLocalize hyphen form ("pl-PL") so callers needn't normalize first.
 *
 * @param {string|null|undefined} languageTag
 * @returns {string|null}
 */
export function getPostcodeRegexForLocale(languageTag) {
    const tag = normalizeLocaleTag(languageTag);
    return tag ? POSTCODE_REGEX_BY_LOCALE[tag] || null : null;
}

/**
 * Returns the i18n error-message key for a station locale's postcode format, or null when
 * the locale is unknown/missing/generic-English.
 *
 * @param {string|null|undefined} languageTag
 * @returns {string|null}
 */
export function getPostcodeErrorKeyForLocale(languageTag) {
    const tag = normalizeLocaleTag(languageTag);
    return tag ? POSTCODE_ERROR_KEY_BY_LOCALE[tag] || null : null;
}
